<template>
  <app-cell
    class="passport-cell"
    :edit="!!form"
    :deletable="false"
    :loading="!!form && form.isLoading"
    @edit="onEdit"
    @save="onSave"
    @cancel="onCancel"
  >
    <template slot="content" v-if="passport">
      <div class="passport-cell__content">
        <h2 class="passport-cell__title">
          {{ passport.title }}
        </h2>
        <p class="passport-cell__price">
          Price:
          <b>{{ passport.price }}</b>
          <strong class="usd">({{ currentUsdPrice }})</strong>
        </p>

        <base-stack direction="row">
          <slider class="passport-cell__slider" v-if="passport.imageUrls.length" :images="passport.imageUrls" />
          <base-stack>
            <html-renderer :html="passport.description" />

            <base-stack class="passport-cell__box">
              <p class="passport-cell__entry">
                <span class="entry__label">EU Shopify</span>
                <span class="entry__value">
                  {{ passport.variant?.shopifyIdEU }}
                </span>
              </p>

              <p class="passport-cell__entry">
                <span class="entry__label">US Shopify</span>
                <span class="entry__value">
                  {{ passport.variant?.shopifyIdUS }}
                </span>
              </p>

              <template v-if="passport.video">
                <p class="passport-cell__entry">
                  <span class="entry__label">AWS S3 file path</span>
                  <span class="entry__value">
                    <a :href="passport.video.url" target="_blank">{{ passport.video.fileKey }}</a>
                    <span>{{ ' (' }}</span>
                    <a @click="$emit('watch', passport.video.asset.streamUrl)">MUX</a>
                    <span>)</span>
                  </span>
                </p>
                <p class="passport-cell__entry">
                  <span class="entry__label">Length:</span>
                  <span class="entry__value">
                    <span v-if="passport.video.asset.durationInSeconds">{{
                      passport.video.asset.durationInSeconds | duration
                    }}</span>
                    <span v-else>Unknown</span>
                  </span>
                </p>
              </template>
            </base-stack>
          </base-stack>
        </base-stack>
      </div>
    </template>
    <template slot="form" v-if="!!form">
      <base-stack as="form" @submit.prevent class="passport-cell__form">
        <base-form-input class="form__input" label="Title">
          <input v-model="form.title" placeholder="Title" />
        </base-form-input>
        <base-form-input class="form__input" label="Price">
          <input v-model.number="form.price" placeholder="Price" type="number" />
          <template slot="hint">{{
            inputUsdPrice ? `= ${inputUsdPrice}` : 'In USD x 1000, i.e. $100.00 = 100000'
          }}</template>
        </base-form-input>
        <base-form-input label="Product image" class="form__input">
          <input @change="onFileUpload" placeholder="Image" type="file" accept="image/png, image/jpeg" multiple />
        </base-form-input>
        <base-form-input class="form__input" label="Description">
          <app-html-editor v-model="form.description" />
        </base-form-input>

        <base-stack class="form__video">
          <p class="form__description">
            Upload the video to Amazon S3 in root bucket
            <i>('avatar-country-dev' for test environment)</i>. Use the file name below, and save form at which point
            MUX starts doing it's compression.
          </p>
          <base-form-input label="Video file path on Amazon S3" class="form__input">
            <input v-model="form.videoFileKey" placeholder="early-entry.mp4" />
          </base-form-input>
        </base-stack>

        <base-form-input class="form__input" label="EU Shopify">
          <input v-model="form.shopifyIdEU" placeholder="Shopify id (EU)" />
        </base-form-input>
        <base-form-input class="form__input" label="US Shopify">
          <input v-model="form.shopifyIdUS" placeholder="Shopify id (US)" />
        </base-form-input>
      </base-stack>
    </template>
  </app-cell>
</template>

<script lang="ts">
import AppCell from '@/components/Cell.vue';
import AppHtmlEditor from '@/components/HtmlEditor.vue';
import HtmlRenderer from '@/components/HtmlRenderer.vue';
import Slider from '@/components/Slider.vue';
import { UpdatePassportForm } from '@/forms/purchasable/UpdatePassportForm';
import { getFilesFromUploadEvent } from '@/utils/getFilesFromUploadEvent';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Passport } from '../types/api';

@Component({
  components: { AppCell, AppHtmlEditor, HtmlRenderer, Slider },
})
export default class PassportCell extends Vue {
  @Prop({ required: true })
  passport!: Passport;

  public form: UpdatePassportForm | null = null;

  public get currentUsdPrice(): string {
    const price = this.passport.price;
    return `$${price / 1000}`;
  }

  public get inputUsdPrice() {
    const price = this.form?.price;
    return price ? `$${price / 1000}` : null;
  }

  public onEdit() {
    this.form = new UpdatePassportForm(this.passport);
  }

  public onCancel() {
    this.closeForm();
  }

  public async onSave() {
    await this.form?.save();
    this.closeForm();
  }

  public onFileUpload(e: HTMLFileUploadEvent) {
    const files = getFilesFromUploadEvent(e);
    this.form!.images = files;
  }

  private closeForm() {
    this.form = null;
    this.$emit('close');
  }
}
</script>

<style lang="scss" scoped>
.passport-cell {
  &__title {
    strong {
      margin-left: 8px;
      font-size: 1rem;
    }
  }
  &__price {
    margin-bottom: 16px;
    b {
      font-weight: bold;
    }
    strong {
      margin-left: 8px;
    }
  }

  &__box {
    width: 500px;
    max-width: 80vw;
    padding: 12px 16px;
    margin-bottom: 8px;
    border: 1px solid $color-theme-red;

    .entry {
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
      column-gap: 8px;

      &__value {
        float: right;
        font-weight: bold;
        strong {
          margin-left: 2px;
          font-size: 1rem;
        }

        button {
          background: none;
          border: none;
          color: $color-theme-red;
          cursor: pointer;
          font-size: inherit;
          font-weight: inherit;
          text-decoration: underline;
          padding: 0;
        }

        &.danger {
          color: $color-theme-red;
        }
        &.success {
          color: $color-success;
        }
      }
    }
  }
  &__entry {
    margin: 0;
    margin-bottom: 4px;
    .entry__label {
      font-weight: bold;
      margin-right: 4px;
    }
    a {
      cursor: pointer;
      text-decoration: underline;
    }
  }
  &__slider {
    flex-shrink: 0;
    height: 220px;
    width: 240px;
    max-width: 400px;
    margin-right: 16px;
  }

  &__form {
    flex: 1;
    max-width: 600px;
    display: flex;
    flex-direction: column;

    .form__video {
      padding: 12px 16px;
      border: 1px solid $color-theme-red;
    }
  }
}
</style>
